/** @jsx tsx */
import * as _ from "lodash";
import { tsx } from "@dojo/framework/core/vdom";
import { DNode } from "@dojo/framework/core/interfaces";
import { DimensionResults, Dimensions } from "@dojo/framework/core/meta/Dimensions";
import { WidgetBase } from "@dojo/framework/core/WidgetBase";
import { defaultsDeep } from "lodash";
import * as Constants from "../../Constants";
import { generateUUID, IRectSizeBox, IShapeBox } from "../../store/reducers/util";
import { ICustomShapePoint } from "../interfaces/model/dataStructures/CustomShapePoint";
import { GeometryTypes } from "../interfaces/model/enumerations/GeometryTypes";
import { CanvasMode, IPageObjectTypes, Keys, LineCap } from "../interfaces/model/enumerations/index";
import { ICustomGeometry, IPenGeometry, IHandlePoint, HandleType } from "../interfaces/model/geometry/CustomGeometry";
import { ISnapSvg } from "../interfaces/model/SnapSvg";
import { createPosition, IPosition } from "../meta/Drag/CsiDragController";
import { getCustomGeometry } from "../selectors/geometry";
import { getSnapValue } from "../selectors/index";
import { getSnapPosition, getZoomPosition } from "../selectors/stateAwareConstants";
import * as util from "../util";
import { ICreateShapeRenderProps } from "./CanvasCreateShape";
import { MarkerObject } from "./MarkerObject";

// declaring types for snapsvg
declare const Snap: ISnapSvg;

const MIN_DISTANCE = 5;

const withinMinDistance = (firstPoint: IPosition, secondPoint: IPosition) => {
	return util.getDistance(firstPoint, secondPoint) <= MIN_DISTANCE;
};

/**
 * Canvas Draw custom shape.
 * Here we use optimistic rendering, a shape is rendered before adding to the state.
 * This way user can see shape before it get's added, as well it will get added in single operation.
 */
export class CreatePenShape extends WidgetBase<ICreateShapeRenderProps> {
	/**
	 * Marker head id
	 */
	private markerHeadID = "";

	/**
	 * Marker tail id
	 */
	private markerTailID = "";

	/**
	 * Key for drawing board.
	 * @type {string}
	 */
	private key = "drawingBoard";

	/**
	 * Key for the shape being drawn, required to retrieve dimensions for the shape.
	 * @type {string}
	 */
	private shapeKey = Constants.TEMP_SHAPE;

	/**
	 * Store dimensions for the drawing board.
	 * @type {DimensionResults}
	 */
	private drawingBoardDimensions: DimensionResults | undefined = undefined;

	/**
	 * First point in the pen tool shape to be created
	 * @type {IPosition}
	 */
	private firstPoint?: IPosition;

	/**
	 * Second point in the pen tool shape to be created
	 * @type {IPosition}
	 */
	private secondPoint?: IPosition;

	/**
	 * Handle associated with the first point
	 * @type {IHandlePoint}
	 */
	private firstHandle?: IHandlePoint;

	/**
	 * Handle associated with the second point
	 * @type {IHandlePoint}
	 */
	private secondHandle?: IHandlePoint;

	/**
	 * Set to true when the first point and its handles are finished
	 * @type {boolean}
	 */
	private firstPointFinished?: boolean;

	// Whether the created shape is going to be a line.  False if it has handles
	private useLine: boolean;

	private isDragging: boolean;

	private shiftKeyPressed?: boolean;

	/**
	 * Custom shape defaults props
	 */
	private shapeDefaultProps: { [key: string]: any } = {};

	/**
	 * Target page index for which shape creation started.
	 * This is useful to guard shape creation against page selection change when in draw mode.
	 */
	private targetPageId: string | undefined = undefined;

	/**
	 * Holds event handlers registered for this widget. When widget is detached, these handlers will be removed.
	 */
	private _boundHandlers: any[] = [];

	constructor() {
		super();
		this._boundHandlers = [];

		// Add event listeners for create custom shape
		[
			{ event: "pointermove", func: this._onPointerMove.bind(this) },
			{ event: "keydown", func: this._onKeyDown.bind(this) },
			{ event: "keyup", func: this._onKeyUp.bind(this) },
		].forEach((object) => {
			document.addEventListener(object.event as any, object.func);
			this._boundHandlers.push(object);
		});
	}

	/**
	 * Remove all event listener
	 */
	protected onDetach(): void {
		this._boundHandlers.forEach((object) => document.removeEventListener(object.event, object.func));
	}

	/**
	 * Key down event on document
	 * @param event {KeyboardEvent}
	 */
	protected _onKeyDown(event: KeyboardEvent) {
		const isEscapeKey = event.keyCode === Keys.ESCAPE;
		const isEnterKey = event.keyCode === Keys.ENTER;
		const isShiftKey = event.keyCode === Keys.SHIFT;
		if (isEnterKey || isEscapeKey) {
			this.addCurveOnPage(isEscapeKey);
		}
		if (isShiftKey) {
			this.shiftKeyPressed = true;
		}
	}

	/**
	 * Key up event on document
	 * @param event {KeyboardEvent}
	 */
	protected _onKeyUp(event: KeyboardEvent) {
		const isShiftKey = event.keyCode === Keys.SHIFT;
		if (isShiftKey) {
			this.shiftKeyPressed = false;
		}
	}

	/**
	 * Pointer Up event for shape
	 * @param event {PointerEvent}
	 */
	protected _onPointerDown(event: PointerEvent) {
		this.isDragging = true;
		let point = {
			x: event.clientX,
			y: event.clientY,
		};

		// get X,Y location for click
		const x = getSnapValue(event.clientX - this.drawingBoardDimensions.position.left);
		const y = getSnapValue(event.clientY - this.drawingBoardDimensions.position.top);

		const editorBoundary: IRectSizeBox = {
			bottom: this.drawingBoardDimensions.position.bottom,
			height: this.drawingBoardDimensions.size.height,
			left: this.drawingBoardDimensions.position.left,
			right: this.drawingBoardDimensions.position.right,
			top: this.drawingBoardDimensions.position.top,
			width: this.drawingBoardDimensions.size.width,
		};
		const positionWithinEditor = util.adjustPositionWithinEditor({ x, y }, editorBoundary, true, true);

		// apply zoom factor
		point = getSnapPosition(getZoomPosition(positionWithinEditor));

		if (this.firstPoint) {
			if (this.shiftKeyPressed) {
				point = util.calculateAngleSnappedPoint(point, this.firstPoint);
			}
		}

		if (this.firstPoint && !this.secondPoint && withinMinDistance(point, this.firstPoint)) {
			this.secondPoint = point;
		}

		if (!this.firstPoint) {
			this.addFirstPoint(point);
		}
	}

	/**
	 * Pointer Up event for shape
	 * @param event {PointerEvent}
	 */
	protected _onPointerUp(event: PointerEvent) {
		let point = {
			x: event.clientX,
			y: event.clientY,
		};

		const x = getSnapValue(event.clientX - this.drawingBoardDimensions.position.left);
		const y = getSnapValue(event.clientY - this.drawingBoardDimensions.position.top);

		const editorBoundary: IRectSizeBox = {
			bottom: this.drawingBoardDimensions.position.bottom,
			height: this.drawingBoardDimensions.size.height,
			left: this.drawingBoardDimensions.position.left,
			right: this.drawingBoardDimensions.position.right,
			top: this.drawingBoardDimensions.position.top,
			width: this.drawingBoardDimensions.size.width,
		};
		point = util.adjustPositionWithinEditor({ x, y }, editorBoundary, true, true);

		if (this.shiftKeyPressed) {
			point = util.calculateAngleSnappedPoint(point, this.firstPoint);
		}

		if (this.firstPoint && this.firstPointFinished) {
			if (withinMinDistance(getZoomPosition(point), this.secondPoint)) {
				this.useLine = true;
				this.secondHandle = {
					type: HandleType.STRAIGHT,
				};
				this.addCurveOnPage(true);
			} else if (this.secondHandle) {
				this.useLine = false;
				this.addCurveOnPage(true);

				// Reset shape draw tools on shape draw complete
				this.properties.api.selectionApi.setShapeDrawTool();

				this.firstPoint = undefined;
				this.secondPoint = undefined;
				this.firstHandle = undefined;
				this.secondHandle = undefined;
			}
		} else {
			if (withinMinDistance(getZoomPosition(point), this.firstPoint)) {
				this.firstHandle = {
					type: HandleType.STRAIGHT,
				};
			}
			this.firstPointFinished = true;
		}

		this.isDragging = false;
	}

	/**
	 * Add drawn curve to actual page.
	 * @param editOnComplete {boolean | undefined}
	 */
	protected addCurveOnPage(editOnComplete?: boolean) {
		const shapeToAdd = this._completeShape();
		if (shapeToAdd) {
			const { canvasApi, pageApi } = this.properties.api;

			// Add shape to actual pageObjects
			pageApi.addPageObject(shapeToAdd);

			// Reset canvas mode to select or edit mode depending on key pressed or not
			if (editOnComplete) {
				canvasApi.editPageObject();
			} else {
				canvasApi.setCanvasMode(CanvasMode.SELECT, true);
			}
		}

		// Reset shape draw tools on shape draw complete
		this.properties.api.selectionApi.setShapeDrawTool();
		this.firstPoint = undefined;
		this.secondPoint = undefined;
		this.firstHandle = undefined;
		this.secondHandle = undefined;
	}

	/**
	 * On point move on drawing add new point to the shape.
	 * @param {PointerEvent} event
	 */
	protected _onPointerMove(event: PointerEvent) {
		const { canvasContainerEl } = this.properties.api;
		this.drawingBoardDimensions = this.meta(Dimensions)
			.get(this.key);
		// get X,Y location for click
		const x = getSnapValue(event.clientX - this.drawingBoardDimensions.position.left);
		const y = getSnapValue(event.clientY - this.drawingBoardDimensions.position.top);

		const positionWithinEditor = util.adjustPositionWithinEditor({ x, y }, canvasContainerEl.getBoundingClientRect(), true, true);

		// apply zoom factor
		let position = getZoomPosition(positionWithinEditor);
		if (this.shiftKeyPressed) {
			position = util.calculateAngleSnappedPoint(position, this.secondPoint);
		}

		if (!this.secondPoint && this.firstPoint && this.isDragging) {
			this.firstHandle = {
				frontHandle: position,
				backHandle: undefined,
				type: HandleType.MIRRORED,
			};
			this.invalidate();
		} else if (this.secondPoint && this.isDragging) {
			this.secondHandle = {
				frontHandle: position,
				backHandle: undefined,
				type: HandleType.MIRRORED,
			};
			this.invalidate();
		} else if (this.firstPoint) {
			this.secondPoint = position;

			this.invalidate();
		}
	}

	/**
	 * Override WidgetBase#render to produce a virtual DOM tree.
	 * @returns {HNode} Each time render() executes, it should build the entire virtual DOM tree.
	 */
	protected render() {
		this.drawingBoardDimensions = this.meta(Dimensions)
			.get(this.key);
		return (
			<div
				classes="c8_canvas_modeDraw"
				key={this.key}
				onpointerdown={this._onPointerDown}
				onpointerup={this._onPointerUp}
				styles={{ overflow: "visible", zIndex: `${Constants.MAX_Z_INDEX}` }}
				touch-action="auto"
			>
				<svg
					height={`${Constants.PERCENTAGE_HUNDRED}%`}
					styles={{ overflow: "visible" }}
					width={`${Constants.PERCENTAGE_HUNDRED}%`}
					x="0"
					y="0"
				>
					{this.renderDefs()}
					<g>
						{this.renderShape()}
						{this.renderHandles()}
					</g>
				</svg>

			</div>
		);
	}

	/**
	 * Add marker if line has headEnd and headTail properties
	 * @return {MarkerObject | null}
	 */
	protected renderDefs(): DNode {
		if (this.shapeDefaultProps.spPr && this.shapeDefaultProps.spPr.ln) {
			return (
				<MarkerObject
					lineProps={this.shapeDefaultProps.spPr.ln}
					markerHeadID={this.markerHeadID}
					markerTailID={this.markerTailID}
				/>
			);
		}
		return null;
	}

	private renderHandles() {
		this.useLine = true;
		const points = [] as ICustomShapePoint[];
		if (this.firstPoint && this.firstHandle && !this.firstPointFinished) {
			const handlePoint = this.firstHandle.frontHandle;
			points.push({ isCurvePoint: true, position: handlePoint } as ICustomShapePoint);

			const secondHandlePoint = {
				x: this.firstPoint.x - (handlePoint.x - this.firstPoint.x),
				y: this.firstPoint.y - (handlePoint.y - this.firstPoint.y),
			};

			points.push({ isCurvePoint: true, position: getSnapPosition(secondHandlePoint) } as ICustomShapePoint);
		} else if (this.secondPoint && this.secondHandle) {
			const handlePoint = this.secondHandle.frontHandle;
			points.push({ isCurvePoint: true, position: handlePoint } as ICustomShapePoint);

			const secondHandlePoint = {
				x: this.secondPoint.x - (handlePoint.x - this.secondPoint.x),
				y: this.secondPoint.y - (handlePoint.y - this.secondPoint.y),
			};

			points.push({ isCurvePoint: true, position: getSnapPosition(secondHandlePoint) } as ICustomShapePoint);
		}
		if (!points.length) {
			return null;
		}
		const operations = util.generateShapePathList(points, true, false, true);

		// create shape geometry
		const shapeGeometry: ICustomGeometry = {
			// add MoveTo path operation
			pathLst: [{
				operations,
			}],
			type: GeometryTypes.CUSTOM,
		};

		// user defaults
		const stroke = Constants.CURVE_EDIT_STROKE_COLOR;
		const strokeWidth = 1;
		const dash = this.shapeDefaultProps.spPr.ln.dash;
		// generate geometry for the shape.
		const geometryParams = getCustomGeometry({
			geometry: shapeGeometry,
			shapeAvList: undefined,
		});
		return (
			<path
				d={`${geometryParams.pathInfo.pathString}`}
				fill="none"
				key={`${"handle" + this.shapeKey}`}
				marker-end={this.markerTailID && `url(#${this.markerTailID})`}
				marker-start={this.markerHeadID && `url(#${this.markerHeadID})`}
				stroke={`${stroke}`}
				stroke-dasharray={`${Constants.STROKE_DASH_MAPPING[dash]}`}
				stroke-width={`${strokeWidth}`}
				vector-effect="non-scaling-stroke"
			/>
		);
	}

	/**
	 * Render shape to be drawn.
	 * @returns {DNode}
	 */
	private renderShape() {
		// If more than 1 points added
		this.useLine = true;
		if (this.firstPoint && this.secondPoint) {
			const points = [] as ICustomShapePoint[];
			points.push({ isCurvePoint: true, position: this.firstPoint } as ICustomShapePoint);
			points.push({ isCurvePoint: true, position: this.secondPoint } as ICustomShapePoint);
			if (this.firstHandle && !(this.firstHandle.type == HandleType.STRAIGHT)) {
				const handlePoint = this.firstHandle.frontHandle;
				const secondHandlePoint: IPosition = { x: 0, y: 0 };
				secondHandlePoint.x = this.secondPoint.x - (handlePoint.x - this.secondPoint.x);
				secondHandlePoint.y = this.secondPoint.y - (handlePoint.y - this.secondPoint.y);

				this.firstHandle.backHandle = secondHandlePoint;
				this.useLine = false;
			}
			if (this.secondHandle && !(this.secondHandle.type == HandleType.STRAIGHT)) {
				const handlePoint = this.secondHandle.frontHandle;
				const secondHandlePoint: IPosition = { x: 0, y: 0 };
				secondHandlePoint.x = this.secondPoint.x - (handlePoint.x - this.secondPoint.x);
				secondHandlePoint.y = this.secondPoint.y - (handlePoint.y - this.secondPoint.y);

				this.secondHandle.backHandle = secondHandlePoint;
				this.useLine = false;
			}

			const handlePoints: IHandlePoint[] = [this.firstHandle, this.secondHandle];
			const operations = util.generatePenOperationList(points, handlePoints, false);

			// create shape geometry
			const shapeGeometry: IPenGeometry = {
				// add MoveTo path operation
				handlePoints: handlePoints,
				pathLst: [{
					operations,
				}],
				type: GeometryTypes.PEN,
			};

			const stroke = this.secondHandle ? Constants.CURVE_EDIT_STROKE_COLOR : Constants.HANDLE_COLOR;
			const strokeWidth = 1;
			const dash = this.shapeDefaultProps.spPr.ln.dash;
			// generate geometry for the shape.
			const geometryParams = getCustomGeometry({
				geometry: shapeGeometry,
				shapeAvList: undefined,
			});
			return (
				<path
					d={`${geometryParams.pathInfo.pathString}`}
					fill="none"
					key={`${this.shapeKey}`}
					marker-end={this.markerTailID && `url(#${this.markerTailID})`}
					marker-start={this.markerHeadID && `url(#${this.markerHeadID})`}
					stroke={`${stroke}`}
					stroke-dasharray={`${Constants.STROKE_DASH_MAPPING[dash]}`}
					stroke-width={`${strokeWidth}`}
					vector-effect="non-scaling-stroke"
				/>
			);
		}
		return null;
	}

	/**
	 * Adds first point to custom shape path.
	 * @param {IPosition} point the point to be added to shape path
	 */
	private addFirstPoint(point: IPosition) {
		this.shapeDefaultProps.spPr = {};
		// this.drawingBoardDimensions = this.meta(Dimensions).get(this.key);
		const shapeDefaults = this.properties.selection.defaultShapeProperties as { [key: string]: any };
		let lineProps = shapeDefaults && shapeDefaults.ln;
		let fill = shapeDefaults && shapeDefaults.spPr.fill;
		// Merge current properties with shape defaults properties
		lineProps = defaultsDeep(lineProps, Constants.DEFAULT_SHAPE_PROPERTIES.spPr.ln,
			lineProps);
		fill = defaultsDeep(fill, Constants.DEFAULT_SHAPE_PROPERTIES.spPr.fill);
		this.shapeDefaultProps.spPr.fill = fill;
		this.shapeDefaultProps.spPr.ln = lineProps;

		// Default arrow head
		const { headEnd, tailEnd } = this.shapeDefaultProps.spPr.ln;
		const isHead = (headEnd && headEnd !== LineCap.NO_ARROW);
		const isTail = (tailEnd && tailEnd !== LineCap.NO_ARROW);
		this.markerHeadID = isHead ? `${generateUUID()}_headID` : "";
		this.markerTailID = isTail ? `${generateUUID()}_tailID` : "";

		this.targetPageId = this.properties.page.id;
		this.firstPoint = getZoomPosition(getSnapPosition(point));
	}

	/**
	 * Shape drawing is complete, now we need to scale, translate shape as per shape bounds.
	 */
	private _completeShape() {
		const {
			firstHandle,
			firstPoint,
			secondHandle,
			secondPoint,
		} = this;

		// Get dimensions for the drawn shape,
		// we need to update offset and scale shape for 10 inches in EMU (english metric unit)
		if (firstPoint && secondPoint && firstHandle && secondHandle) {
			const points = [] as ICustomShapePoint[];
			points.push({ isCurvePoint: true, position: firstPoint } as ICustomShapePoint);
			points.push({ isCurvePoint: true, position: secondPoint } as ICustomShapePoint);

			const handlePoints = [firstHandle, secondHandle] as IHandlePoint[];
			const operations = util.generatePenOperationList(points, handlePoints, false);

			// create shape geometry
			const shapeGeometry: IPenGeometry = {
				handlePoints,
				// add MoveTo path operation
				pathLst: [{
					operations,
				}],
				type: GeometryTypes.PEN,
			};
			// generate geometry for the shape.
			const geometryParams = getCustomGeometry({
				geometry: shapeGeometry,
				shapeAvList: undefined,
			});
			const pathString = geometryParams.pathInfo.pathString;
			// SnapSvg path.getBBox method gives path box by providing path string,
			// also this library method supports for all modern browsers.
			const curveBox: IShapeBox = Snap.path.getBBox(pathString);
			// curve shape box
			const left = curveBox.x;
			const top = curveBox.y;
			const { height, width } = curveBox;

			shapeGeometry.handlePoints = [firstHandle, secondHandle];

			const tempShape = {
				allowsArrows: true,
				penShape: true,
				shapeId: generateUUID(),
				spPr: {
					geometry: shapeGeometry,
					ln: {
						W: 1,
						dash: this.shapeDefaultProps.spPr.ln.dash,
						fill: {
							color: this.shapeDefaultProps.spPr.ln.fill.color,
						},
						headEnd: this.shapeDefaultProps.spPr.ln.headEnd,
						tailEnd: this.shapeDefaultProps.spPr.ln.tailEnd,
					},
					xfrm: {
						ext: { cx: 0, cy: 0 },
						flipH: false,
						flipV: false,
						off: {
							x: 0,
							y: 0,
						},
						rot: 0,
					},
				},
				type: IPageObjectTypes.SHAPE,
			};
			const { ext, off } = tempShape.spPr.xfrm;

			// set shape dimensions.
			ext.cx = width;
			ext.cy = height;

			const { canvasContainerEl } = this.properties.api;
			util.adjustPositionForRoot(canvasContainerEl, off, { x: left, y: top });

			// we need to adjust offsets & scale each point from all path operation list
			const pathLst = tempShape.spPr.geometry.pathLst;
			const pathOperationLst = pathLst && pathLst[0] && pathLst[0].operations;

			if (pathLst && pathOperationLst) {
				// Here we are using 914400 (10 inches in EMU (english metric unit) ) as out computation basis.
				// Shape is drawn based on this value.
				util.updateShapePathList(pathLst, -1 * left, -1 * top);
				if (firstHandle.backHandle) {
					firstHandle.backHandle.x -= left;
					firstHandle.backHandle.y -= top;
				}
				if (firstHandle.frontHandle) {
					firstHandle.frontHandle.x -= left;
					firstHandle.frontHandle.y -= top;
				}
				if (secondHandle.backHandle) {
					secondHandle.backHandle.x -= left;
					secondHandle.backHandle.y -= top;
				}
				if (secondHandle.frontHandle) {
					secondHandle.frontHandle.x -= left;
					secondHandle.frontHandle.y -= top;
				}
			}
			return tempShape;
		}
		return;
	}
}

export default CreatePenShape;
